<script lang="ts">
  /**
   * Small chat-bubble greeting shown next to the floating button on page
   * load. Click on the bubble itself opens the panel; the X button only
   * dismisses the bubble without opening the chat.
   */
  interface Props {
    message: string;
    assistantName?: string;
    assistantAvatarUrl?: string;
    closeLabel: string;
    onclick: () => void;
    onclose: () => void;
  }

  let {
    message,
    assistantName,
    assistantAvatarUrl,
    closeLabel,
    onclick,
    onclose,
  }: Props = $props();

  let avatarBroken = $state(false);
  $effect(() => {
    void assistantAvatarUrl;
    avatarBroken = false;
  });
  const showAvatar = $derived(
    !!assistantAvatarUrl && assistantAvatarUrl.length > 0 && !avatarBroken,
  );
</script>

<div class="hd-welcome">
  <button
    type="button"
    class="hd-welcome__close"
    aria-label={closeLabel}
    onclick={(e) => {
      e.stopPropagation();
      onclose();
    }}
  >
    <svg viewBox="0 0 24 24" width="10" height="10" aria-hidden="true">
      <path
        d="M6 6l12 12M18 6L6 18"
        fill="none"
        stroke="currentColor"
        stroke-width="2.4"
        stroke-linecap="round"
      />
    </svg>
  </button>
  <button type="button" class="hd-welcome__body" {onclick}>
    {#if showAvatar}
      <img
        class="hd-welcome__avatar"
        src={assistantAvatarUrl}
        alt=""
        aria-hidden="true"
        onerror={() => (avatarBroken = true)}
      />
    {:else if assistantName}
      <span class="hd-welcome__avatar hd-welcome__avatar--initial" aria-hidden="true">
        {assistantName[0]?.toUpperCase() ?? '?'}
      </span>
    {/if}
    <span class="hd-welcome__text">{message}</span>
  </button>
</div>

<style>
  .hd-welcome {
    position: relative;
    max-width: 280px;
    animation: hd-welcome-in 220ms cubic-bezier(0.2, 0.8, 0.2, 1);
  }
  .hd-welcome__body {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 10px 14px 10px 12px;
    border: none;
    border-radius: 14px;
    background: var(--hd-surface, #ffffff);
    color: var(--hd-fg, #0f172a);
    text-align: left;
    cursor: pointer;
    box-shadow:
      0 12px 28px -10px rgb(0 0 0 / 0.22),
      0 3px 8px -2px rgb(0 0 0 / 0.1),
      0 0 0 1px rgb(15 23 42 / 0.05);
    font: inherit;
    font-size: 13px;
    line-height: 1.35;
    transition:
      transform 160ms cubic-bezier(0.2, 0.8, 0.2, 1),
      box-shadow 160ms cubic-bezier(0.2, 0.8, 0.2, 1);
  }
  .hd-welcome__body:hover {
    transform: translateY(-1px);
    box-shadow:
      0 14px 32px -10px rgb(0 0 0 / 0.26),
      0 4px 10px -2px rgb(0 0 0 / 0.12),
      0 0 0 1px rgb(15 23 42 / 0.06);
  }
  .hd-welcome__body:focus-visible {
    outline: 2px solid var(--hd-primary, #2563eb);
    outline-offset: 2px;
  }
  .hd-welcome__avatar {
    flex-shrink: 0;
    width: 28px;
    height: 28px;
    border-radius: 9999px;
    object-fit: cover;
    background: var(--hd-primary, #2563eb);
    color: var(--hd-primary-fg, #ffffff);
    display: grid;
    place-items: center;
    font-size: 12px;
    font-weight: 600;
  }
  .hd-welcome__text {
    min-width: 0;
    word-break: break-word;
  }
  .hd-welcome__close {
    position: absolute;
    top: -4px;
    right: -4px;
    width: 18px;
    height: 18px;
    border-radius: 9999px;
    border: none;
    cursor: pointer;
    background: rgb(15 23 42 / 0.82);
    color: rgb(255 255 255 / 0.92);
    display: grid;
    place-items: center;
    box-shadow:
      0 1px 4px rgb(0 0 0 / 0.16),
      0 0 0 1.5px var(--hd-surface, #ffffff);
    transition:
      transform 140ms cubic-bezier(0.2, 0.8, 0.2, 1),
      background-color 140ms cubic-bezier(0.2, 0.8, 0.2, 1);
  }
  .hd-welcome__close:hover {
    transform: scale(1.05);
    background: rgb(15 23 42 / 0.92);
  }
  .hd-welcome__close:focus-visible {
    outline: 2px solid var(--hd-primary, #2563eb);
    outline-offset: 2px;
  }
  @keyframes hd-welcome-in {
    from {
      opacity: 0;
      transform: translateY(6px) scale(0.96);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
  @media (prefers-reduced-motion: reduce) {
    .hd-welcome {
      animation: none;
    }
    .hd-welcome__body,
    .hd-welcome__close {
      transition: none;
    }
  }
</style>
